import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { RootStore } from '~/stores/root.store';
import { TCommand, CommandType } from '~/types/builder.types';
import { FlowApplyActionsErrors } from '~/types/server.types';
import { Action } from './action';
import { cloneDeep } from 'lodash';
import { toBinaryBase64 } from '~/utils/text.utils';

export function mapActionNumberToAction(
    actionNumbers: Array<number>,
    actions: Array<Action>,
    fallBackActions: Array<Action>
): Record<number, Action> {
    return mapActionIdToActionByParam<number>(
        'action_number',
        actionNumbers,
        actions,
        fallBackActions
    );
}

export function mapActionIdToAction(
    actionIds: Array<string>,
    actions: Array<Action>,
    fallBackActions: Array<Action>
): Record<number, Action> {
    return mapActionIdToActionByParam<string>(
        '_id',
        actionIds,
        actions,
        fallBackActions
    );
}

export function mapActionIdToActionByParam<T>(
    paramName: string,
    actionsParamValues: Array<T>,
    actions: Array<Action>,
    fallBackActions: Array<Action>
): Record<number, Action> {
    const actionIdToAction = {};
    actionsParamValues.forEach(actionParamValue => {
        let action = actions.find(x => x[paramName] === actionParamValue);
        if (!action) {
            // In case that the action was override we will use the fallback action array
            action = fallBackActions.find(
                x => x[paramName] === actionParamValue
            );
        }

        actionIdToAction[action[paramName]] = action;
    });

    return actionIdToAction;
}

export interface MultiStageIdParts {
    itemId?;
    blockId?: string;
    pageId?: string;
    actionName: string;
}

export function calculateMultiStageActionId({
    itemId,
    blockId,
    pageId,
    actionName
}: MultiStageIdParts) {
    const idParts = [itemId, blockId, pageId, actionName];

    return idParts.filter(x => x).join('-');
}

export function validateCommand(cmd: TCommand, rootStore: RootStore) {
    const { data, type } = cmd;
    // we currently only validate comands that update children
    if (type !== CommandType.Update || !('children' in data)) {
        return true;
    }

    const blocksMap =
        rootStore.builderUiStore.selectedFlow?.draftRevision.blocksMap;
    const parent = blocksMap[data.block_id];
    const children = data.children
        .map(child => blocksMap[child.block_id])
        .filter(Boolean);

    const isValid = children.every(block => {
        try {
            if (!parent.canBeParentOf(block)) {
                const error = new Error(FlowApplyActionsErrors.invalidParent);
                // @ts-ignore
                error.parent = { type: parent.type, blockId: parent.block_id };
                // @ts-ignore
                error.child = { type: block.type, blockId: block.block_id };
                throw error;
            }
        } catch (e) {
            if (e?.message === 'HBInvalidBlockParentError') {
                rootStore.toastStore.add({
                    type: ToastType.Error,
                    text: `Invalid block assignment. Block of type ${e?.child?.type} cannot be child of block of type ${e?.parent?.type}`
                });

                return false;
            }
        }

        return true;
    });

    return isValid;
}

// Encode html content on contractHtmlBlock
// First - encode to char binary UTF-16
// Second - encode Base64
export function encodeHtmlOnApplyActionCommandContent(actions) {
    const newActions = cloneDeep(actions);
    newActions.forEach(singleAction => {
        const commands = (singleAction?.updated_blocks || []).concat(
            singleAction?.created_blocks || []
        );
        commands.forEach(block => {
            if (block?.contract?.html_body) {
                const html_body = block?.contract?.html_body;
                block.contract.html_body = toBinaryBase64(html_body);
            }
            if (block?.type == 'text_block' && !!block?.content) {
                const html_body = block?.content;
                block.content = toBinaryBase64(html_body);
            }
        });
    });

    return newActions;
}
